import React from 'react'
import { motion } from 'framer-motion'
import { EmptyProps } from 'lib/types'
import { Paper } from 'components/Paper'
import { useRouter } from 'next/router'
import Image from 'next/image'

const Index: React.FC<EmptyProps> = () => {
  const router = useRouter()

  return (
    <div>
      <div className='flex flex-row px-10 py-4 items-center space-x-2'>
        <div className='pt-2 text-3xl'>
          <Image src='/supercharge_logo.svg' width={40} height={40} />
        </div>

        <div className='text-2xl font-bold text-gray-900 z-10'>
          Super<span className='text-indigo-700'>charge</span>
        </div>
      </div>
      <div className='relative z-20'>
        <header className='py-10'>
          <div className='max-w-7xl mx-auto px-24 space-y-6'>
            <motion.div
              animate={{ opacity: [0, 1] }}
              transition={{ duration: 1 }}
            >
              <button
                type='button'
                onClick={() => router.push('/')}
                className='inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
              >
                Back
              </button>
              <div className='relative px-4 sm:px-6 lg:px-8'>
                <div className='text-lg max-w-prose mx-auto'>
                  <h1>
                    <span className='block text-base text-center text-indigo-600 font-semibold tracking-wide uppercase'>
                      SUPERCHARGE
                    </span>
                    <span className='mt-2 block text-3xl text-center leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl'>
                      User agreement
                    </span>
                  </h1>
                  <div className='text-lg text-center max-w-prose mx-auto'>
                    <p className='mt-8 text-xl text-gray-500'>
                      <strong>Last updated July 20, 2022</strong>
                    </p>
                  </div>
                </div>
                <div className='relative px-4 sm:px-6 lg:px-8'>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    This User Agreement is between you (referenced herein as
                    “you”, “your”, or “user”) and Supercharge Software, Inc.,
                    and its affiliates (“we”, “our” or “Supercharge”). This User
                    Agreement (the “Agreement”) governs your use of the services
                    provided by Supercharge described below (the “Services”)
                    accessible through the Supercharge website or application
                    and the API (the “Site”). By accessing, using, or attempting
                    to access or use the Services, you acknowledge and agree
                    that you have read, understood and accepted all of the terms
                    and conditions contained in this Agreement as well as our
                    Privacy Policy. If you do not agree, you may not access or
                    use the Services and must immediately cease any use of the
                    Services. We may amend this Agreement from time to time by
                    posting a revised version of this Agreement to the Site and
                    sending notice to the email associated with your Account (if
                    applicable), and may do so without advance notice to you.
                    Accordingly, please review the Agreement posted at this
                    location on a periodic basis. Each time you use the
                    Services, you agree to be bound by the terms of the
                    Agreement in effect at the time of your use thereof. If you
                    do not agree to the revised terms, you are not permitted to
                    use the Services.
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    <strong>
                      THIS AGREEMENT CONTAINS IMPORTANT PROVISIONS INCLUDING AN
                      ARBITRATION PROVISION THAT REQUIRES YOU AND SUPERCHARGE TO
                      RESOLVE ALL DISPUTES BY BINDING ARBITRATION INSTEAD OF IN
                      COURT, UNLESS YOU CHOOSE TO OPT OUT OF SUCH PROVISION.
                      PLEASE SEE SECTION 15 TITLED “DISPUTE RESOLUTION BY
                      BINDING ARBITRATION AND CLASS ACTION WAIVER” BELOW. UNLESS
                      YOU OPT OUT OF THE ARBITRATION AGREEMENT, YOU WAIVE YOUR
                      RIGHT TO TRIAL BY JURY, AND EXCEPT AS PERMITTED BY SECTION
                      15, YOU WILL NOT BE ABLE TO BRING ANY CLAIMS IN STATE OR
                      FEDERAL COURT.
                    </strong>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    <strong>
                      AS EXPLAINED IN FURTHER DETAIL IN THE SECTION BELOW
                      ENTITLED “E-SIGN DISCLOSURE AND CONSENT,”AUTHORIZATION TO
                      ELECTRONICALLY RECEIVE ANY AND ALL COMMUNICATIONS (DEFINED
                      BELOW) IS A CONDITION OF ACCESS TO AND USE OF THE
                      SERVICES, MEANING THAT IF YOU REVOKE YOUR CONSENT TO
                      RECEIVE SUCH COMMUNICATIONS ELECTRONICALLY YOU UNDERSTAND
                      THAT YOU MAY NOT CONTINUE TO ACCESS OR USE THE SERVICES.
                    </strong>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    <strong>
                      As with any asset, the value of digital currencies can go
                      up or down and there can be a substantial risk that you
                      lose money buying, selling, holding, or investing in
                      digital currencies. You should carefully consider whether
                      trading or holding digital currencies is suitable for you
                      in light of your financial condition. Supercharge is not
                      registered with the U.S. Securities and Exchange
                      Commission and does not offer securities services in the
                      United States or to U.S. persons.
                    </strong>
                  </div>
                  <br />
                  <h1>
                    <span className='block text-base text-center text-indigo-600 font-semibold tracking-wide uppercase'>
                      <strong>PART A: GENERAL TERMS </strong>
                    </span>
                  </h1>
                  <div className='text-lg text-center max-w-prose mx-auto'>
                    <p className='mt-8 text-xl text-gray-500'>
                      <strong>1. THE SERVICES.</strong>
                    </p>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    <ul role='list'>
                      <li>
                        {' '}
                        1. Description of Services. Supercharge offers a variety
                        of Services to users, including the Core Services,
                        described in Part B of this Agreement. The Core Services
                        allow a user to exchange fiat and/or digital currency
                        via third party services using the Site (see Part B).
                        Some of these services are executed via third parties.
                        We do not offer the Services in all jurisdictions.
                        Additional information regarding the Services may be
                        available and obtained through the Site. The Services,
                        and certain aspects of the Services, may, as applicable,
                        be delayed, restricted, forfeited, or ultimately
                        unavailable due to certain laws and regulations
                        governing our Services as well as certain circumstances
                        and conditions associated with your use of the Services.
                      </li>
                      <li>
                        {' '}
                        2. Eligibility. In order to use the Services, you must
                        be a resident of a jurisdiction we serve and be at least
                        eighteen (18) years of age (or the applicable age of
                        majority and contractual capacity in the jurisdiction in
                        which you reside).
                      </li>
                    </ul>
                  </div>
                  <div className='text-lg text-center max-w-prose mx-auto'>
                    <p className='mt-8 text-xl text-gray-500'>
                      <strong>2. IDENTITY VERIFICATION.</strong>
                    </p>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    <ul role='list'>
                      <li>
                        {' '}
                        1. Identity Verification. In order to use the Services,
                        you agree to provide us, and / or third parties we hire,
                        with the information we request for the purposes of
                        verifying your identity and detecting potential money
                        laundering, terrorist financing, fraud or other
                        financial crimes and permit us to keep a record of such
                        information. You will need to complete certain
                        verification procedures before you are permitted to use
                        the Services. Your access to the Services and the limits
                        that apply to your use of the Services, may be altered
                        as a result of information collected about you on an
                        ongoing basis. The information we request may include
                        certain personal information, including, but not limited
                        to, your name, address, telephone number, email address,
                        date of birth, taxpayer identification number, a
                        government identification, and information regarding
                        your bank account (such as the name of the bank, the
                        account type, routing number, and account number). In
                        providing us with this or any other information that may
                        be required, you confirm that the information is
                        accurate and authentic. You agree to keep us updated if
                        any of the information you provide to us changes. You
                        authorize us to make inquiries, whether directly or
                        through third parties, that we consider necessary to
                        verify your identity or protect you and/or us against
                        fraud or other financial crime, and to take action we
                        reasonably deem necessary based on the results of such
                        inquiries. When we carry out these inquiries, you
                        acknowledge and agree that your personal information may
                        be disclosed to credit reference and fraud prevention or
                        financial crime agencies and that these agencies may
                        respond to our inquiries in full. This is an identity
                        check only and should have no adverse effect on your
                        credit rating. See our Privacy Policy for how we treat
                        your data.
                      </li>
                      <li>
                        2. OFAC Screening and Sanctions. Supercharge complies
                        with the economic sanctions programs administered by the
                        U.S. Department of the Treasury’s Office of Foreign
                        Assets Control (“OFAC”). As part of the identity
                        verification process, Supercharge and / or third parties
                        we hire will check your information against the OFAC
                        Specially Designated Nationals and Blocked Persons List
                        (“SDN List”) and ensure that you are not operating in a
                        jurisdiction subject to sanctions. Supercharge will
                        periodically check the information you provide to us
                        against the SDN List to comply with our obligations
                        under federal law. Supercharge is not responsible for
                        any losses, whether direct or indirect, that you may
                        incur as a result of our compliance with applicable law,
                        the guidance or direction of any regulatory authority or
                        government agency, or any writ of attachment, lien,
                        levy, subpoena, warrant or other legal order.
                      </li>
                    </ul>
                  </div>

                  <div className='text-lg text-center max-w-prose mx-auto'>
                    <p className='mt-8 text-xl text-gray-500'>
                      <strong>3. PROHIBITED ACTIVITIES.</strong>
                    </p>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    <ul role='list'>
                      <li>
                        1. Illegal Activities. You are responsible for complying
                        with applicable law, including state and federal laws
                        and regulations regarding money laundering, terrorist
                        financing, and money transfer and remittance. You are
                        responsible for understanding and abiding by the laws
                        and regulations of each jurisdiction in which you use
                        the Services.
                      </li>
                      <li>
                        2. Service Limitations. Except as required by law, we
                        may, without notice and without liability to you,
                        suspend or terminate access to, or refuse to provide,
                        any Services at any time in our sole discretion,
                        including with limitation:
                      </li>
                      <ul>
                        <li>
                          - if we believe, in our sole discretion, you directly
                          or indirectly use, or attempt to use, the Services for
                          any unlawful or improper purpose;
                        </li>
                        <li>
                          - if we believe you have engaged in any activity that
                          is prohibited on the Site;
                        </li>
                        <li>
                          - if you provide any incomplete, incorrect or false
                          information to us;
                        </li>
                        <li>
                          - if you attempt to tamper, hack, modify or otherwise
                          corrupt the security or functionality of the Site or
                          the Services;
                        </li>
                        <li>
                          - if we believe that your use of any method of payment
                          is unauthorized, or if your method of payment does not
                          have sufficient available funds (or credit, as
                          applicable), or that has expired, or if your method of
                          payment is declined or your payment is blocked or
                          reversed for any reason;
                        </li>
                        <li>
                          - if you have breached any portion of this Agreement;
                        </li>
                        <li>
                          - if we determine such action is necessary to comply
                          with this Agreement, any of our policies, procedures
                          or practices, or any law, rule or regulation; and/or
                        </li>
                        <li> - as otherwise set forth in Section 10.</li>
                      </ul>
                    </ul>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    You agree that we will not be held responsible or liable to
                    you or any other person for such action except as required
                    by law.
                  </div>

                  <div className='text-lg text-center max-w-prose mx-auto'>
                    <p className='mt-8 text-xl text-gray-500'>
                      <strong>4. FEES.</strong>
                    </p>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    Supercharge charges certain fees for the Services, which are
                    described in the applicable Part of this Agreement. Any fees
                    imposed will also be displayed to you as part of the
                    transaction process. Supercharge is not responsible for any
                    fees charged by a third party.
                  </div>

                  <div className='text-lg text-center max-w-prose mx-auto'>
                    <p className='mt-8 text-xl text-gray-500'>
                      <strong>5. ERRORS AND REFUNDS.</strong>
                    </p>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    Supercharge may provide refunds in certain circumstances in
                    its sole discretion. If you believe an error has occurred or
                    you would like to cancel your transaction, please contact
                    hello@supercharge.finance.
                  </div>
                  <div className='text-lg text-center max-w-prose mx-auto'>
                    <p className='mt-8 text-xl text-gray-500'>
                      <strong>6. THIRD PARTY DELAYS ON ERRORS.</strong>
                    </p>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    Supercharge is not responsible for any delays or errors in
                    the provision of the Services, including, but not limited
                    to, delays in the transfer of digital or fiat currency from
                    your wallet to a third party, if such delays or errors are
                    caused by you, your service provider (e.g., your bank), your
                    internet connection or internet service provider.
                  </div>
                  <div className='text-lg text-center max-w-prose mx-auto'>
                    <p className='mt-8 text-xl text-gray-500'>
                      <strong>7. NOTICE.</strong>
                    </p>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    Any notice required to be given by Supercharge under this
                    Agreement, or otherwise, may be provided to you by email, a
                    posting on the Site, or other reasonable means. Supercharge
                    may broadcast, distribute or display notices or messages
                    through the Site or via email to inform you of changes to
                    this Agreement, the Services, the Privacy Policy or other
                    matters of importance. Such broadcasts, distributions or
                    displays of information shall constitute notice to you. Your
                    continued use of the Site or the Services subsequent to such
                    notification shall be deemed an acknowledgement and
                    acceptance thereof.
                  </div>
                  <div className='text-lg text-center max-w-prose mx-auto'>
                    <p className='mt-8 text-xl text-gray-500'>
                      <strong>8. DATA AND PRIVACY.</strong>
                    </p>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    All collection, sharing, and use of data regarding access to
                    the Site and usage of the Services is governed by our
                    Privacy Policy.
                  </div>
                  <div className='text-lg text-center max-w-prose mx-auto'>
                    <p className='mt-8 text-xl text-gray-500'>
                      <strong>9. INTELLECTUAL PROPERTY.</strong>
                    </p>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    Supercharge reserves all rights in and to the Site and
                    Services and all related intellectual property.
                    “Supercharge,” and all associated logos, trademarks or other
                    identifiers displayed within the Site are the sole property
                    of Supercharge or its licensors. You acknowledge that, as
                    between you and Supercharge, all rights, title and interest,
                    including all copyright, trademark, patent, trade secret and
                    other intellectual property or proprietary rights, related
                    to the Site (including all modifications, improvements,
                    upgrades, and derivative works thereof) belong exclusively
                    to Supercharge. You shall honor and comply with any and all
                    contractual, statutory or common law rights of Supercharge,
                    as well as any applicable third parties, arising out of or
                    relating to the provision or use of the Site or Services.
                  </div>
                  <div className='text-lg text-center max-w-prose mx-auto'>
                    <p className='mt-8 text-xl text-gray-500'>
                      <strong>10. INTERACTIONS WITH THIRD PARTIES.</strong>
                    </p>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    Any website or third party service that is accessible in the
                    Site or as part of the Services are not within Supercharge’s
                    control and we are not responsible for the content,
                    products, services, or terms of any linked website or third
                    party service. All such websites and third-party services
                    are subject to the terms, conditions, policies, and
                    procedures of the owners of such websites and not this
                    Agreement. Accordingly, Supercharge hereby expressly
                    disclaims and shall not have any liability or responsibility
                    for the content, the materials, the accuracy of the
                    information, and/or the quality of the products or services
                    provided by, available through, or advertised on any such
                    websites or third party services accessible on the Site. We
                    encourage you to read the applicable terms and conditions in
                    this Agreement to understand your rights and obligations.
                  </div>
                  <div className='text-lg text-center max-w-prose mx-auto'>
                    <p className='mt-8 text-xl text-gray-500'>
                      <strong>11. TERMINATION.</strong>
                    </p>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    Supercharge, in its sole discretion, may suspend or
                    terminate your access to or use of the Services, for any
                    reason, including, without limitation, if we receive a
                    facially valid subpoena, court order or other binding order
                    from a government authority requiring us to do so or if we
                    believe you have violated this Agreement, in letter or in
                    spirit. Supercharge may also, in its sole discretion,
                    discontinue providing the Services, or any part thereof,
                    with or without notice.
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    All provisions of this Agreement which, according to their
                    terms or their nature, should survive termination,
                    including, without limitation, provisions with respect to
                    limitation or liability and disclaimer of warranties, shall
                    survive.
                  </div>
                  <div className='text-lg text-center max-w-prose mx-auto'>
                    <p className='mt-8 text-xl text-gray-500'>
                      <strong>12. INDEMNIFICATION.</strong>
                    </p>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    TO THE FULLEST EXTENT PERMITTED BY LAW, YOU AGREE TO
                    INDEMNIFY AND HOLD SUPERCHARGE, ITS AFFILIATES, AND ITS
                    PARENT, SUBSIDIARIES, OFFICERS, EMPLOYEES, AGENTS,
                    DIRECTORS, MANAGERS, EQUITY OWNERS, SUCCESSORS AND ASSIGNS
                    (COLLECTIVELY, THE “SUPERCHARGE GROUP”), AS APPLICABLE,
                    HARMLESS FROM ANY CLAIM OR DEMAND, INCLUDING REASONABLE
                    ATTORNEYS' FEES, DUE TO OR ARISING OUT OF YOUR USE OF THE
                    SERVICES, THE VIOLATION OF THIS AGREEMENT BY YOU, THE
                    INFRINGEMENT BY YOU (OR ANYONE ACCESSING THE SITE USING YOUR
                    ACCOUNT) OF ANY INTELLECTUAL PROPERTY OR OTHER RIGHT OF ANY
                    PERSON OR ENTITY OR VIOLATION OF ANY APPLICABLE LAW OR
                    REGULATION.
                  </div>
                  <div className='text-lg text-center max-w-prose mx-auto'>
                    <p className='mt-8 text-xl text-gray-500'>
                      <strong>13. LIMITATION OF LIABILITY.</strong>
                    </p>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    IN ADDITION, TO THE FULLEST EXTENT PERMITTED BY LAW,
                    SUPERCHARGE SHALL NOT BE LIABLE FOR ANY INDIRECT,
                    INCIDENTAL, SPECIAL OR CONSEQUENTIAL DAMAGES, INCLUDING BUT
                    NOT LIMITED TO DAMAGES FOR LOSS OF PROFITS, USE, DATA OR
                    OTHER INTANGIBLES, EVEN IF SUPERCHARGE HAS BEEN ADVISED OF
                    THE POSSIBILITY OF SUCH DAMAGES, INCLUDING DAMAGES RESULTING
                    FROM (I) THE USE OR THE INABILITY TO USE THE SERVICES; (II)
                    THE COST OF PROCUREMENT OF SUBSTITUTE SERVICES; OR (III)
                    UNAUTHORIZED ACCESS TO YOUR ACCOUNT OR ALTERATION OF YOUR
                    ACCOUNT OR DATA. YOU SPECIFICALLY AGREE THAT SUPERCHARGE IS
                    NOT RESPONSIBLE OR LIABLE TO YOU OR ANYONE ELSE FOR ANY LOSS
                    OF, ON, OR THROUGH THE SITE OR THE SERVICES, OR FOR ANY
                    INFRINGEMENT OR VIOLATION OF YOUR RIGHTS BY ANY OTHER PARTY,
                    INCLUDING BUT NOT LIMITED TO INTELLECTUAL PROPERTY RIGHTS,
                    RIGHTS OF PUBLICITY, OR RIGHTS OF PRIVACY. SUPERCHARGE IS
                    NOT LIABLE FOR ANY CHANGES IN VALUE IN DIGITAL CURRENCY OR
                    FIAT CURRENCY. SUPERCHARGE’S TOTAL LIABILITY TO YOU FOR
                    BREACH OF CONTRACT AND FOR ANY AND ALL OTHER CLAIMS
                    (INCLUDING TORT CLAIMS) ARISING OUT OF OR IN CONNECTION WITH
                    THIS AGREEMENT, THE SITE OR THE SERVICES, SHALL NOT EXCEED
                    THE TOTAL AMOUNT OF ALL PAYMENTS MADE BY YOU TO SUPERCHARGE
                    HEREUNDER. SOME JURISDICTIONS DO NOT ALLOW THE LIMITATION OR
                    EXCLUSION OF LIABILITY FOR INCIDENTAL OR CONSEQUENTIAL
                    DAMAGES, THEREFORE SOME OF THE ABOVE LIMITATIONS MAY NOT
                    APPLY TO YOU.
                  </div>
                  <div className='text-lg text-center max-w-prose mx-auto'>
                    <p className='mt-8 text-xl text-gray-500'>
                      <strong>14. DISCLAIMER OF WARRANTIES.</strong>
                    </p>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    YOU EXPRESSLY AGREE THAT YOUR USE OF THE SITE AND SERVICES
                    ARE AT YOUR SOLE RISK AND EXPENSE AND SUBJECT TO THIS
                    AGREEMENT, ANY APPLICABLE LAW, AND ANY ADDITIONAL TERMS AND
                    CONDITIONS IMPOSED BY ANY ISSUER OF DIGITAL CURRENCY OR
                    OTHER THIRD PARTY. THE SITE AND SERVICES ARE PROVIDED ON AN
                    “AS IS” AND “AS AVAILABLE” BASIS. TO THE MAXIMUM EXTENT
                    PERMITTED BY APPLICABLE LAW, SUPERCHARGE EXPRESSLY DISCLAIMS
                    ALL WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED,
                    INCLUDING, BUT NOT LIMITED TO THE IMPLIED WARRANTIES OF
                    MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
                    NON-INFRINGEMENT OR ANY OTHER IMPLIED WARRANTY UNDER THE
                    UNIFORM COMPUTER INFORMATION TRANSACTIONS ACT AS ENACTED BY
                    ANY STATE. SUPERCHARGE MAKES NO WARRANTY THAT THE SITE OR
                    SERVICES WILL MEET YOUR REQUIREMENTS, OR THAT THE SITE OR
                    SERVICES WILL BE UNINTERRUPTED, TIMELY, SECURE, OR ERROR
                    FREE. SUPERCHARGE MAKES NO WARRANTY REGARDING ANY
                    TRANSACTIONS ENTERED INTO THROUGH THE SITE. NO INFORMATION,
                    WHETHER ORAL OR WRITTEN, OBTAINED BY YOU FROM THE
                    SUPERCHARGE GROUP SHALL CREATE ANY WARRANTY NOT EXPRESSLY
                    MADE HEREIN.
                  </div>
                  <div className='text-lg text-center max-w-prose mx-auto'>
                    <p className='mt-8 text-xl text-gray-500'>
                      <strong>
                        15. DISPUTE RESOLUTION BY BINDING ARBITRATION AND CLASS
                        ACTION WAIVER
                      </strong>
                    </p>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    AGREEMENT TO ARBITRATE, SEAT OF THE ARBITRATION,
                    ARBITRATOR’S AUTHORITY AND FORM OF THE AWARD. EXCEPT FOR
                    DISPUTES THAT QUALIFY FOR SMALL CLAIMS COURT, ALL DISPUTES
                    ARISING OUT OF OR RELATED TO THIS AGREEMENT OR ANY ASPECT OF
                    THE RELATIONSHIP BETWEEN YOU AND US, WHETHER BASED IN
                    CONTRACT, TORT, STATUTE, FRAUD, MISREPRESENTATION, OR ANY
                    OTHER LEGAL THEORY, WILL BE RESOLVED THROUGH FINAL AND
                    BINDING ARBITRATION BEFORE A SINGLE, NEUTRAL ARBITRATOR,
                    INSTEAD OF IN A COURT BY A JUDGE OR JURY, AND WE AND YOU
                    EACH WAIVE THE RIGHT TO TRIAL BY AJURY. THE SEAT OF THE
                    ARBITRATION SHALL BE DOVER, DELAWARE, NOTWITHSTANDING YOUR
                    RIGHT TO CHOOSE WHERE ARBITRATION HEARINGS WILL BE
                    CONDUCTED, AS FURTHER PROVIDED HEREIN. THE ARBITRATION SHALL
                    BE CONDUCTED IN ENGLISH. THE ARBITRATOR SHALL BE A
                    PRACTICING ATTORNEY. THE ARBITRATOR SHALL APPLY THE LAW AND
                    WILL ISSUE A FINAL AND BINDING AWARD STATING THE REASONS FOR
                    THE ARBITRATOR’S DECISION AND A CALCULATION OF ANY DAMAGES
                    AWARDED. A JUDGMENT ON THE AWARD MAY BE ENTERED BY ANY COURT
                    HAVING JURISDICTION. THE ARBITRATOR SHALL HAVE THE AUTHORITY
                    TO DECIDE ISSUES OF ARBITRABILITY, SHALL DECIDE THE RIGHTS
                    AND LIABILITIES, IF ANY, OF YOU AND WE. THE ARBITRATOR SHALL
                    HAVE THE AUTHORITY TO GRANT MOTIONS THAT ARE DISPOSITIVE OF
                    ALL OR PART OF ANY CLAIM OR DISPUTE. THE ARBITRATOR WILL
                    HAVE THE AUTHORITY TO AWARD MONETARY DAMAGES AND TO GRANT
                    ANY NON-MONETARY REMEDY OR RELIEF AVAILABLE UNDER APPLICABLE
                    LAW AND THIS AGREEMENT. THE ARBITRATOR SHALL HAVE THE SAME
                    AUTHORITY TO AWARD RELIEF THAT A JUDGE IN A COURT OF LAW
                    WOULD HAVE.
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    WAIVER OF CLASS ARBITRATION OR ACTIONS. ANY CLAIMS YOU OR WE
                    ASSERT UNDER THIS AGREEMENT WILL BE BROUGHT ON AN INDIVIDUAL
                    BASIS AND NOT ON A CLASS, CONSOLIDATED, REPRESENTATIVE OR
                    COLLECTIVE BASIS. ONLY INDIVIDUAL RELIEF IS AVAILABLE FOR
                    CLAIMS COVERED BY THIS ARBITRATION AGREEMENT, AND YOU AND WE
                    AGREE THAT CLASS ARBITRATIONS AND CLASS ACTIONS ARE NOT
                    PERMITTED UNDER ANY CIRCUMSTANCES, INCLUDING THAT ANY CLAIMS
                    OF MORE THAN ONE CUSTOMER CANNOT BE ARBITRATED OR
                    CONSOLIDATED WITH THOSE OF ANOTHER CUSTOMER OR PERSON. YOU
                    AND WE AGREE TO GIVE UP THE ABILITY TO PARTICIPATE IN ANY
                    FORM OF CLASS ARBITRATION AND/OR ACTION. EACH PARTY
                    KNOWINGLY, VOLUNTARILY, AND INTENTIONALLY WAIVES ANY AND ALL
                    RIGHTS IT MAY HAVE TO A TRIAL BY JURY. ANY DISPUTE MUST BE
                    INITIATED WITHIN ONE YEAR AFTER THE COMPLAINING PARTY
                    DISCOVERS THE FACTS THAT FORM THE BASIS FOR THE CONTROVERSY
                    OR CLAIM, OR IT IS FOREVER WAIVED.
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    APPLICABLE ARBITRATION RULES, HEARINGS AND COSTS.
                    ARBITRATION WILL BE ADMINISTERED BY JAMS IN ACCORDANCE WITH
                    THE THEN-CURRENT COMPREHENSIVE ARBITRATION RULES &
                    PROCEDURES (THE “RULES”), INCLUDING THE EXPEDITED PROCEDURES
                    SET FORTH IN THOSE RULES. THESE RULES ARE AVAILABLE AT
                    WWW.JAMSADR.COM OR BY CALLING JAMS AT (800) 352-5267. ANY
                    REQUIRED ARBITRATION HEARING MAY BE CONDUCTED, AT YOUR
                    OPTION: (1) IN SAN FRANCISCO COUNTY, CALIFORNIA; (2) IN A
                    LOCATION OF YOUR CHOOSING THAT IS WITHIN 50 MILES OF YOUR
                    PLACE OF HABITUAL RESIDENCE; (3) IN A MUTUALLY AGREEABLE
                    ALTERNATIVE LOCATION; OR (4) BY TELEPHONE OR VIDEO
                    CONFERENCE. THE PLACE WHERE THE ARBITRATION HEARINGS SHALL
                    BE CONDUCTED WILL HAVE NO BEARING ON THE LEGAL SEAT OF THE
                    ARBITRATION, WHICH SHALL AT ALL TIMES REMAIN DOVER,
                    DELAWARE. THE SOLE ARBITRATOR SHALL BE APPOINTED BY
                    AGREEMENT OF THE PARTIES. IF SUCH AN AGREEMENT CANNOT BE
                    REACHED WITHIN THIRTY DAYS, THEN THE ARBITRATOR WILL BE
                    APPOINTED BY JAMS IN ACCORDANCE WITH THE RULES. IF YOU
                    INITIATE ARBITRATION HEREUNDER, YOU SHALL PAY UP TO $250 OF
                    THE COSTS OF THE ARBITRATION AND WE PAY ALL COSTS OF THE
                    ARBITRATION IN EXCESS THEREOF (TO THE EXCLUSION OF ANY
                    ATTORNEYS’ FEES YOU INCUR, WHICH SHALL EXCLUSIVELY BE BORNE
                    BY YOU UNLESS AND UNTIL AWARDED BY THE ARBITRATOR IN THE
                    FINAL AWARD). WHERE WE INITIATE ARBITRATION AGAINST YOU, WE
                    SHALL PAY ALL COSTS OF THE ARBITRATION. THE PREVAILING PARTY
                    WILL BE ENTITLED TO AN AWARD OF THE COSTS AND EXPENSES OF
                    THE ARBITRATION, INCLUDING ATTORNEYS’ FEES AND EXPERT
                    WITNESS FEES TO THE FULLEST EXTENT PERMITTED BY LAW.
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    EXCEPTIONS TO ARBITRATION. YOU AND WE AGREE THAT THE
                    FOLLOWING DISPUTES (AND ONLY THESE DISPUTES) ARE NOT SUBJECT
                    TO THE ABOVE PROVISIONS CONCERNING BINDING ARBITRATION AND
                    MAY BE BROUGHT IN ANY COURT HAVING JURISDICTION OVER THE
                    PARTIES AND SUBJECT MATTER: (1) ANY CLAIMS THAT QUALIFY FOR
                    DISPOSITION BY A SMALL CLAIMS COURT; (2) ANY SUIT TO COMPEL
                    ARBITRATION, STAY PROCEEDING PENDING ARBITRATION, OR TO
                    CONFIRM, MODIFY, VACATE OR ENTER JUDGMENT ON THE AWARD
                    ENTERED BY THE ARBITRATOR; AND (3) ANY SUIT TO SEEK
                    TEMPORARY INJUNCTIVE RELIEF THAT WILL REMAIN IN PLACE ONLY
                    UNTIL AN ARBITRATOR CAN DETERMINE WHETHER THE RELIEF SHOULD
                    BE CONTINUED, MODIFIED OR REMOVED.
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    SEVERABILITY. YOU AND WE AGREE THAT IF ANY PORTION OF THIS
                    SECTION 15 IS FOUND ILLEGAL OR UNENFORCEABLE, THAT PORTION
                    SHALL BE SEVERED AND THE REMAINDER OF THIS SECTION 15 SHALL
                    BE GIVEN FULL FORCE AND EFFECT.
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    OPT-OUT. YOU HAVE THE RIGHT TO OPT OUT OF THE PROVISIONS OF
                    THIS SECTION 15 BY SENDING A TIMELY WRITTEN NOTICE OF YOUR
                    DECISION BY EMAIL TO hello@supercharge.finance, WITHIN 30
                    DAYS AFTER ENTERING INTO THIS AGREEMENT. YOUR NOTICE MUST
                    INCLUDE YOUR NAME AND ADDRESS AND A CLEAR STATEMENT THAT YOU
                    WANT TO OPT OUT OF THIS SECTION 15 OF THE AGREEMENT. IF YOU
                    OPT OUT OF THIS SECTION 15, ALL OTHER PARTS OF THIS
                    AGREEMENT WILL CONTINUE TO APPLY TO YOU. OPTING OUT OF THIS
                    SECTION 15 WILL NOT HAVE ANY EFFECT ON OTHER ARBITRATION
                    AGREEMENTS THAT YOU MAY CURRENTLY HAVE WITH SUPERCHARGE, OR
                    MAY ENTER INTO IN THE FUTURE WITH SUPERCHARGE.
                  </div>
                  <div className='text-lg text-center max-w-prose mx-auto'>
                    <p className='mt-8 text-xl text-gray-500'>
                      <strong>16. GENERAL TERMS.</strong>
                    </p>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    <ul role='list'>
                      <li>
                        1. Entire Agreement. This Agreement constitutes the
                        entire agreement between you and Supercharge and governs
                        your use of the Services, superseding any prior
                        agreements between you and Supercharge with respect to
                        your use of the Site and Services. You may also be
                        subject to additional terms and conditions that may
                        apply when you use third party services in conjunction
                        with your use of the Services, and you represent and
                        warrant that you will comply with all such additional
                        terms and conditions.
                      </li>
                      <li>
                        2. Governing Law and Disputes. This Agreement is
                        governed by and construed in accordance with the laws of
                        the State of Delaware, excluding its conflicts of laws
                        provisions. To the extent any claims may be made in
                        court pursuant to this Agreement, and subject to the
                        arbitration agreement contained in Section 15 if you
                        have not opted out of the same, you agree to submit to
                        the personal and exclusive jurisdiction of the state and
                        federal courts located within Delaware. TO THE EXTENT
                        THE DISPUTE RESOLUTION BY BINDING ARBITRATION SECTION
                        ABOVE IS INAPPLICABLE TO A CLAIM OR ACTION, AND WITHOUT
                        PREJUDICE TO SUCH SECTION, EACH OF THE PARTIES HEREBY
                        KNOWINGLY, VOLUNTARILY AND INTENTIONALLY WAIVES ANY
                        RIGHT IT MAY HAVE TO A TRIAL BY JURY IN RESPECT OF ANY
                        LITIGATION (INCLUDING BUT NOT LIMITED TO ANY CLAIMS,
                        COUNTERCLAIMS, CROSS-CLAIMS, OR THIRD PARTY CLAIMS)
                        ARISING OUT OF, UNDER OR IN CONNECTION WITH THIS
                        AGREEMENT; EACH PARTY HERETO CERTIFIES THAT NO
                        REPRESENTATIVE OR AGENT OF EITHER PARTY HAS REPRESENTED,
                        EXPRESSLY OR OTHERWISE, THAT SUCH PARTY WOULD NOT IN THE
                        EVENT OF SUCH LITIGATION, SEEK TO ENFORCE THIS WAIVER OF
                        RIGHT TO JURY TRIAL PROVISION; AND EACH OF THE PARTIES
                        ACKNOWLEDGES THAT THIS SECTION IS A MATERIAL INDUCEMENT
                        FOR THE OTHER PARTY TO ENTER INTO THIS AGREEMENT. You
                        and Supercharge agree that any lawsuit arising out of or
                        related to this Agreement or your use of the Site or the
                        Services, which is brought by you or any third party,
                        must commence within one (1) year after the cause of
                        action arises; otherwise, such cause of action is
                        permanently barred.
                      </li>
                      <li>
                        3. Waiver. Supercharge’s failure to exercise or enforce
                        any right or provision of this Agreement shall not
                        constitute a waiver of such right or provision unless
                        acknowledged and agreed to by Supercharge in writing.
                      </li>
                      <li>
                        4. Force Majeure. No party shall be liable for failure
                        or delay in performing obligations (except for
                        obligations regarding payment of money) set forth in
                        this Agreement, and no party shall be deemed in breach
                        of such obligations, if such failure or delay is due to
                        natural disasters or any cause reasonably beyond the
                        control of such party.
                      </li>
                      <li>
                        5. Third Party Beneficiaries. The provisions of this
                        Agreement are solely for the benefit of you and
                        Supercharge, and, except as specifically provided
                        herein, no third party (including creditors of any
                        party) may seek to enforce, or benefit from, these
                        provisions, or seek redress for any breach or other
                        damage, alleged or proved, under this Agreement.
                      </li>
                      <li>
                        6. Headings. The section titles in this Agreement are
                        solely used for the convenience of the parties and have
                        no legal or contractual significance.
                      </li>
                      <li>
                        7. Severability. If any provision of this Agreement,
                        either in whole or in part, is held to be illegal,
                        invalid, void as against public policy, or unenforceable
                        for any reason under present or future law, such
                        provision shall be fully severable without effect on the
                        remaining provisions, which shall continue in full force
                        and effect and remain legal, valid, effective, and
                        enforceable as if the illegal, invalid, void, or
                        unenforceable provision(s) had never comprised a part of
                        such provision or this Agreement, as applicable. In lieu
                        of the illegal, invalid, void, or unenforceable
                        provision, there shall be added a provision as similar
                        in terms and legal effect to the illegal, invalid, void,
                        or unenforceable provision as may be possible and which
                        may be legal, valid, effective and enforceable.
                      </li>
                      <li>
                        8. Modification of Terms. Supercharge reserves the right
                        to modify or discontinue all or any portion of the
                        Services with or without notice to you. Supercharge will
                        not be liable to you if we modify or discontinue all or
                        any portion of the Services. Supercharge may change the
                        terms and conditions of this Agreement at any time in
                        its sole discretion. Your continued use of the Site or
                        the Services subsequent to such modification shall be
                        deemed an acknowledgment and acceptance thereof.
                      </li>
                      <li>
                        9. Assignment. You may not assign any rights and/or
                        licenses granted under this Agreement. We reserve the
                        right to assign our rights without restriction,
                        including without limitation to any Supercharge’s
                        affiliates or subsidiaries, or to any successor in
                        interest of any business associated with the Services.
                        Any attempted transfer or assignment in violation hereof
                        shall be null and void. Subject to the foregoing, this
                        Agreement will bind and inure to the benefit of the
                        parties, their successors and permitted assigns. In the
                        event that Supercharge is acquired by or merged with a
                        third-party entity, we reserve the right, in any of
                        these circumstances, to transfer or assign the
                        information we have collected from you as part of such
                        merger, acquisition, sale, or other change of control.
                      </li>
                      <li>
                        10. Relationship of the Parties. Supercharge is an
                        independent contractor for all purposes. Nothing in this
                        Agreement shall be deemed or is intended to be deemed,
                        nor shall it cause, you and Supercharge to be treated as
                        partners, joint venturers, or otherwise as joint
                        associates for profit, or either you or Supercharge to
                        be treated as the agent of the other.
                      </li>
                      <li>
                        11. CONTACT INFORMATION. If you have any questions
                        regarding this Agreement and/or the Site, please contact
                        Supercharge at hello@supercharge.finance.
                      </li>
                    </ul>
                  </div>
                  <div className='text-lg text-center max-w-prose mx-auto'>
                    <p className='mt-8 text-xl text-gray-500'>
                      <strong>17. E-SIGN DISCLOSURE AND CONSENT.</strong>
                    </p>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    <ul role='list'>
                      <li>
                        1. Scope of Communications to Be Provided in Electronic
                        Form. You understand and agree that we may provide you
                        with any or all of the following types of communications
                        electronically: (i) legally required disclosures,
                        notices and other communications associated with your
                        access to or use of the Services, including, but not
                        limited to information about fees or charges, and any
                        and all legally required pre- and post-transaction
                        disclosures; (ii) customer service communications; (iii)
                        privacy policies and notices; (iv) changes to this
                        Agreement, (v) statements, information and records
                        regarding your transactions; (vi) information regarding
                        the debiting or charging, as applicable of your selected
                        payment method; (vii) any and all legally required error
                        resolution policies, and responses to claims filed in
                        connection with your access to or use of the Services;
                        (viii) any other communications related to your access
                        to and/or use of the Services, and (ix) with your
                        consent, marketing and other promotional communications
                        (collectively, “Communications”).
                      </li>
                      <li>
                        2. Communications in Writing. All Communications in
                        either electronic or paper format from us to you will be
                        considered “in writing.” You should print or download
                        for your records a copy of this Agreement and any other
                        Communication that is important to you.
                      </li>
                      <li>
                        3. Method of Providing Communications to You in
                        Electronic Form. All Communications that we provide to
                        you in electronic form will be provided either (i) via
                        e-mail, (ii) by access to a web site that we will
                        designate in an e-mail notice we send to you at the time
                        the information is available, or (iii) to the extent
                        permitted by law, on the Site or via SMS text message.
                        You agree to promptly review all Communications sent to
                        you, and that these are reasonable procedures for
                        sending and receiving electronic communications.
                      </li>
                      <li>
                        4. How to Update Your Records. To receive electronic
                        Communications, at the time that you first use the
                        Services, you must provide us with a true, accurate and
                        complete e-mail address and your contact information,
                        and you must promptly notify us of any changes to this
                        information. You can update information (such as your
                        email address) through the Site.
                      </li>
                      <li>
                        5. Hardware and Software Requirements. In order to
                        access, view, and retain electronic Communications that
                        we make available to you, you must have an electronic
                        device that enables access to your e-mail account or a
                        commercially available Internet browser. You may wish to
                        utilize a device that is capable of storing or printing
                        the Communications for your records.
                      </li>
                      <li>
                        6. Requesting Paper Copies. If you have consented to
                        receive electronic Communications, we will not send you
                        a paper copy of any Communication unless we deem it
                        appropriate to do so. You can obtain a paper copy of an
                        electronic Communication by printing it yourself. We
                        reserve the right, but assume no obligation, to provide
                        a paper (instead of electronic) copy of any
                        Communication that you have authorized us to provide
                        electronically. You may obtain a paper copy of any
                        legally-required Communications and or agreements. You
                        may request such a paper copy by contacting Supercharge
                        at hello@supercharge.finance.
                      </li>
                      <li>
                        7. How to Withdraw Consent. You may withdraw your
                        consent to receive Communications in electronic form at
                        any time by contacting hello@supercharge.finance. Any
                        withdrawal of your consent to receive electronic
                        Communications will be effective only after we have
                        received your request for withdrawal and have a
                        reasonable period of time to process such request. In
                        the meantime, you will continue to receive
                        Communications in electronic form. By withdrawing your
                        consent, you will no longer be able to use the Services.
                        Withdrawing consent to receive marketing communications
                        only does not preclude use of the Services, however. If
                        you withdraw your consent, the legal validity and
                        enforceability of prior Communications delivered in
                        electronic form will not be affected, and your previous
                        electronic records will remain accessible for such
                        period as is required under law and in a form that
                        allows the record to be accurately reproduced to all
                        persons who are entitled under law to access the record.
                      </li>
                      <li>
                        8. Federal Law. You acknowledge and agree that your
                        consent to electronic Communications is being provided
                        in connection with a transaction affecting interstate
                        commerce that is subject to the federal Electronic
                        Signatures in Global and National Commerce Act (“E-SIGN
                        Act”), and that you and we both intend that the E-Sign
                        Act apply to the fullest extent possible to validate our
                        ability to conduct business with you by electronic
                        means.
                      </li>
                      <li>
                        9. Termination/Changes. We reserve the right, in our
                        sole discretion, to discontinue the provision of your
                        electronic Communications, or to terminate or change the
                        terms and conditions on which we provide electronic
                        Communications. We will provide you with notice of any
                        such termination or change as required by law.
                      </li>
                    </ul>
                  </div>
                  <div className='text-lg text-center max-w-prose mx-auto'>
                    <p className='mt-8 text-xl text-gray-500'>
                      <strong>18. CONTACT SUPERCHARGE.</strong>
                    </p>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    For questions, concerns, or claims, relating to this
                    Agreement, you may reach us at: hello@supercharge.finance
                  </div>
                  <div className='text-lg text-center max-w-prose mx-auto'>
                    <p className='mt-8 text-xl text-gray-500'>
                      <strong>How to Contact Us</strong>
                    </p>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    If you have questions or concerns regarding this policy, or
                    any feedback that you would like us to consider, please
                    email us at hello@supercharge.finance.
                  </div>
                  <br />
                  <h1>
                    <span className='block text-base text-center text-indigo-600 font-semibold tracking-wide uppercase'>
                      <strong>PART B: SUPERCHARGE CORE SERVICES </strong>
                    </span>
                  </h1>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    This Part B provides the terms of use applicable to your use
                    of the Core Services (defined below) and is part of this
                    Agreement. Unless otherwise specified herein, the terms of
                    Part A are incorporated into this Part B.
                  </div>
                  <div className='text-lg text-center max-w-prose mx-auto'>
                    <p className='mt-8 text-xl text-gray-500'>
                      <strong>1. THE CORE SERVICES.</strong>
                    </p>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    <ul role='list'>
                      <li>
                        1. Description of Core Services. Supercharge enables
                        users to: (i) exchange fiat and/or digital currency via
                        third party providers (we do not touch fiat currencies
                        ourselves); and (ii) purchase and sell certain digital
                        currencies via third party services. A user can engage
                        in transactions using specified payment methods (e.g.,
                        credit card, ACH, digital currency, etc.).
                      </li>
                      <li>
                        Third Parties. As part of the Core Services, Supercharge
                        relies on third parties to provide services, including
                        but not limited to exchange of fiat currency to digital
                        currency, and provision of digital currency wallet
                        services. All interactions by users with these
                        third-party services are subject to Section 10 of Part A
                        above.
                      </li>
                      <li>
                        2. Eligibility. In order to use the Core Services, you
                        must have and use a valid Account. For more information,
                        see Section 2 below for Account creation and validation.
                      </li>
                      <li>
                        3. Service Limitations. Except as required by law, we
                        may, without notice and without liability to you,
                        suspend or deactivate your Account (defined below), or
                        otherwise suspend or terminate access to, or refuse to
                        provide, the Core Services at any time in our sole
                        discretion, including without limitation:
                      </li>
                      <ul>
                        <li>
                          - if we determine that your Account is inactive, or
                        </li>
                        <li>
                          - if your Account has been subject to excessive (as
                          determined in our sole discretion) reversed or
                          rejected transactions or chargebacks.
                        </li>
                      </ul>
                    </ul>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    You agree that we will not be held responsible or liable to
                    you or any other person for such action except as required
                    by law. The above are in addition to those scenarios set
                    forth in Section 3(2) of Part A.
                  </div>
                  <div className='text-lg text-center max-w-prose mx-auto'>
                    <p className='mt-8 text-xl text-gray-500'>
                      <strong>2. YOUR ACCOUNT.</strong>
                    </p>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    <ul role='list'>
                      <li>
                        1. Account Creation. In order to use the Core Services,
                        you must create an account with Supercharge (“Account”).
                        In order to create an Account, you agree to follow the
                        process set forth in Section 2 of Part A. If you are
                        creating an Account on behalf of an entity, you confirm
                        that you are authorized to enter into this Agreement on
                        behalf of such entity.
                      </li>
                      <li>
                        2. MSB Accounts. If you are a money services business,
                        as defined under the Bank Secrecy Act of 1970, as
                        amended, and its implementing regulations, a
                        state-regulated financial institution (not otherwise
                        captured under federal law), or the foreign equivalent
                        thereof (each, an “MSB”), we may request additional
                        information from you in order to create an Account. An
                        MSB must establish and maintain anti-money laundering
                        programs and compliance and risk policies. To the extent
                        permitted by applicable law, MSBs will make those
                        documents available to us for review. Only after
                        Supercharge has conducted this review will we determine
                        whether to create an Account for the MSB user. After
                        Account creation, Supercharge (either ourselves or
                        through a designated representative) reserves the right
                        to conduct periodic audits of MSB users, upon at least
                        30 days’ notice. Such audits may include review of
                        contracts, hardware and software systems, security
                        systems, policies and procedures, and books and records.
                        If you make any material changes to the policies and
                        procedures you have provided to us, please notify us
                        within 30 days of such change and, if permitted by
                        applicable law, make the updated versions available to
                        us.
                      </li>
                      <li>
                        3. Account Security. YOU ARE RESPONSIBLE FOR MAINTAINING
                        THE CONFIDENTIALITY OF YOUR ACCOUNT CREDENTIALS AND ARE
                        FULLY RESPONSIBLE FOR ANY AND ALL ACTIVITIES THAT OCCUR
                        UNDER YOUR ACCOUNT. You agree to notify Supercharge
                        immediately if you know or suspect unauthorized use of
                        your Account or any other security breach you know or
                        suspect, such as loss, theft, unauthorized disclosure or
                        use of your Account or password. Supercharge is not
                        responsible for any losses, damages, costs, expenses or
                        claims that result from stolen or lost passwords or any
                        unauthorized use of your Account. While Supercharge has
                        endeavored to create a secure and reliable Site and Core
                        Services, you should understand that the confidentiality
                        of any communication or material transmitted to/from the
                        Site or Core Services over the Internet or other form of
                        global communication network cannot be guaranteed.
                        Accordingly, Supercharge is not responsible for the
                        accuracy, reliability, or security of any information
                        transmitted to or from the Site or Core Services.
                      </li>
                    </ul>
                  </div>
                  <div className='text-lg text-center max-w-prose mx-auto'>
                    <p className='mt-8 text-xl text-gray-500'>
                      <strong>3. DIGITAL CURRENCY WALLET.</strong>
                    </p>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    Use of the Core Services requires the user to have a wallet
                    for digital currency provided by a third party ("External
                    Wallet"). If you are not sure how to create your own digital
                    currency wallet, please contact: hello@supercharge.finance.
                    All interactions by users with these third-party services
                    are subject to Section 10 of Part A above. Supercharge is
                    not responsible for the security or services provided by
                    these third-party wallets. By providing your External
                    Wallet, you represent and warrant that you are the sole
                    owner and in complete control of the External Wallet.
                  </div>
                  <div className='text-lg text-center max-w-prose mx-auto'>
                    <p className='mt-8 text-xl text-gray-500'>
                      <strong>4. OTC SERVICES.</strong>
                    </p>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    As part of the Core Services, Supercharge enables users to
                    interact with third-party services to execute
                    over-the-counter digital currency and fiat conversion
                    services (“OTC Services”) through which users may engage in
                    transactions with third parties, acting as principal, in
                    which the user purchases or sells certain digital currencies
                    and/or certain fiat currencies in exchange for fiat currency
                    or another digital currency (each such transaction, an “OTC
                    Transaction”). All interactions by users with these
                    third-party services are subject to Section 10 of Part A
                    above. Supercharge is not responsible for the security or
                    services provided by these third-party service providers.
                  </div>
                  <div className='text-lg text-center max-w-prose mx-auto'>
                    <p className='mt-8 text-xl text-gray-500'>
                      <strong>
                        5. YOUR RESPONSIBILITY TO SUPERCHARGE FOR FINES, FEES
                        AND OTHER PENALTIES.
                      </strong>
                    </p>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    YOU ARE RESPONSIBLE FOR AND AGREE TO PAY TO SUPERCHARGE ANY
                    FINES, FEES, LEVIES OR OTHER AMOUNTS THAT MAY BE IMPOSED BY
                    ANY GOVERNMENTAL ENTITY OR OTHER THIRD PARTY AS A RESULT OF
                    YOUR USE OF THE SERVICES OR VIOLATION OF THIS AGREEMENT.
                    Supercharge will promptly notify you of any such amounts
                    due, the cause thereof and the parties involved. The rights
                    in this Section 5 are in addition to the other rights and
                    remedies in this Agreement, including Section 12 of Part A
                    (Indemnification).
                  </div>
                  <div className='text-lg text-center max-w-prose mx-auto'>
                    <p className='mt-8 text-xl text-gray-500'>
                      <strong>6. FEES.</strong>
                    </p>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    Supercharge charges a transaction fee, which is disclosed to
                    you prior to each transaction. Your financial institution,
                    External Wallet or other third-party services may also
                    impose a fee for use of the Services. Supercharge is not
                    responsible for any fees charged by a third party. In
                    addition, it is your responsibility to ensure you have
                    sufficient funds or digital currency in your designated bank
                    account or External Wallet to use the Services. You are
                    responsible for any non-sufficient funds or overdraft fees
                    imposed by third parties.
                  </div>
                  <div className='text-lg text-center max-w-prose mx-auto'>
                    <p className='mt-8 text-xl text-gray-500'>
                      <strong>7. LIMITS.</strong>
                    </p>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    Users may be subject to weekly, monthly and yearly limits
                    (based on total fiat amount) depending on their location.
                    These limits may be imposed by the third-party service
                    providers who partner with Supercharge to provide the
                    Services. If you have any questions about the limits, please
                    contact Supercharge at hello@supercharge.finance.
                  </div>
                  <div className='text-lg text-center max-w-prose mx-auto'>
                    <p className='mt-8 text-xl text-gray-500'>
                      <strong>8. TERMINATION.</strong>
                    </p>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    You may terminate your Account at any time and for any
                    reason by sending an email requesting termination to
                    hello@supercharge.finance. Supercharge will notify you by
                    email when your Account is closed. Upon termination, your
                    right to use the Core Services and access to your Account
                    will immediately end. Supercharge, in its sole discretion,
                    may suspend or terminate your Account or use of the Core
                    Services, for any reason, including, without limitation, if
                    we receive a facially valid subpoena, court order or other
                    binding order from a government authority requiring us to do
                    so or if we believe you have violated this Agreement, in
                    letter or in spirit. Supercharge may also, in its sole
                    discretion, discontinue providing the Core Services, or any
                    part thereof, with or without notice.
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </header>
      </div>

      <style global jsx>{`
        body > div:first-child,
        div#__next,
        div#__next > div {
          height: 100%;
        }
      `}</style>
    </div>
  )
}

export default Index
